import {
  Pocket,
  type DMMoneyProcessingFailedData,
  type DMFetchTransactionsParams,
  type pocketOnCallback
} from '@nsftx/systems-sdk'
import { logService } from '@nsftx/seven-client-retail-utils'
import DMPocketError from '#src/classes/DMPocketError'
import deviceManagementService from './deviceManagementService'
import {
  moneyProcessingPubSub,
  MONEY_PROCESSING_EVENT_ID,
  type DMPocketMoneyEventData,
  type DMPocketMoneyProcessingCallbackData
} from '#src/events/moneyProcessingEvent'

const LOG_PREFIX = '[deviceManagementPocketService]'
const POCKET_MAX_LOAD_TIME = 15_000
let pocket: Pocket
let isPocketModuleReady: boolean = false
let pocketInitPromise: Promise<void> | null = null

const publishMoneyEvent = (moneyEventData: DMPocketMoneyEventData) => {
  logService.debug(`${LOG_PREFIX} Got data from ${moneyEventData.type} event using SystemsSDK`, {
    code: 'T_DM_POCKET_MONEY_PROCESSING',
    details: {
      moneyEventData
    }
  })
  moneyProcessingPubSub.publish(MONEY_PROCESSING_EVENT_ID, moneyEventData)
}

const onMoneyEventHandler: pocketOnCallback = (moneyEventData) => {
  publishMoneyEvent(moneyEventData as unknown as DMPocketMoneyProcessingCallbackData)
}

const onMoneyProcessingFailedHandler = (moneyEventData: DMMoneyProcessingFailedData) => {
  publishMoneyEvent(moneyEventData)
}

const initPocketModule = (): Promise<void> => {
  if (isPocketModuleReady) {
    return Promise.resolve()
  }

  if (pocketInitPromise) {
    return pocketInitPromise
  }

  isPocketModuleReady = false
  const core = deviceManagementService.getCore()
  pocketInitPromise = new Promise<void>((resolve, reject) => {
    const rejectTimeout = window.setTimeout(() => {
      pocketInitPromise = null
      reject(new DMPocketError('Pocket ready event not received', 'T_DM_POCKET_READY_ERROR'))
    }, POCKET_MAX_LOAD_TIME)
    pocket = new Pocket(core)

    pocket.on('ready', () => {
      isPocketModuleReady = true
      window.clearTimeout(rejectTimeout)
      attachPocketListeners()
      resolve()
    })
  })

  return pocketInitPromise
}

const isPocketModuleReadyState = () => isPocketModuleReady

const waitForPocketReady = (): Promise<void> => {
  if (isPocketModuleReady) {
    return Promise.resolve()
  }

  if (pocketInitPromise) {
    return pocketInitPromise
  }

  return Promise.reject(
    new DMPocketError('Pocket not yet initialized', 'T_DM_POCKET_NOT_INITIALIZED')
  )
}

const getPocketInstance = () => {
  if (isPocketModuleReady) return pocket

  throw new DMPocketError('Pocket not yet initialized', 'T_DM_POCKET_NOT_INITIALIZED')
}

const attachPocketListeners = () => {
  const pocketInstance = getPocketInstance()

  pocketInstance.on('moneyProcessing', onMoneyEventHandler)
  pocketInstance.on('moneyProcessed', onMoneyEventHandler)
  pocketInstance.on('moneyUnprocessed', onMoneyEventHandler)
  pocketInstance.on('moneyProcessingFailed', onMoneyProcessingFailedHandler)
}

const fetchTransactions = async (params?: DMFetchTransactionsParams) => {
  await waitForPocketReady()
  return getPocketInstance().fetchTransactions(params)
}

const fetchTransApoens = async () => {
  await waitForPocketReady()
  return getPocketInstance().fetchTransApoens()
}

const emptyCashBox = () => getPocketInstance().emptyCashBox()

const payinCancel = (id: string) => getPocketInstance().payinCancel(id)

const payinRetry = (id: string) => getPocketInstance().payinRetry(id)

const setNote = async (correlationUuid: string, operatorUuid: string, content: string) => {
  await waitForPocketReady()
  return getPocketInstance().setNote(correlationUuid, operatorUuid, content)
}

const _resetForTest = () => {
  isPocketModuleReady = false
  pocketInitPromise = null
  // @ts-expect-error - test-only
  pocket = undefined
}

export default {
  fetchTransactions,
  fetchTransApoens,
  emptyCashBox,
  payinCancel,
  getPocketInstance,
  initPocketModule,
  isPocketModuleReadyState,
  waitForPocketReady,
  attachPocketListeners,
  payinRetry,
  setNote,
  _resetForTest
}
